import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Card, Container, Row, Modal } from 'reactstrap'
import Header from '../../components/Headers/Header'
import { toggleFoodAvailability, deleteFood, updateFoodOrders } from '../../apollo/server'
import FoodModal from './FoodModal'
import CustomLoader from '../../components/Loader/CustomLoader'
import Loader from 'react-loader-spinner'
import { getIsDisabled } from '../../utils/getIsDisabled'
import { useMutation, gql, } from "@apollo/client"
import { SortableTable } from '../../components/SortableTable'
import { arrayMove } from '../../utils/arrayMove'
import styles from '../styles'
import { getRestaurantQuery, useRestaurantQuery } from '../../domain/graphql/query/useRestaurantQuery'
import { PHONE_BOUNDARY } from '../constants'
import { AppContext } from '../../app'
import { SearchBar } from '../../components/SearchBar'

const TOGGLE_FOOD = gql`${toggleFoodAvailability}`
const UPDATE_FOOD_ORDERS = gql`${updateFoodOrders}`
const DELETE_FOOD = gql`${deleteFood}`

export type EnhancedFood = {
  _id: string;
  title: string;
  categoryId: string;
  category: string;
  description: string;
  variations: {
    _id: string;
    title: string;
    price: number;
    taxInPrice: number;
    storeProfit: number;
    discounted: number;
    janCode?: string
    addons: string[];
  }[];
  image: string;
  isActive: boolean;
  isAvailable: boolean;
}
const Food = () => {
  const { t } = useTranslation()
  const appContext = useContext(AppContext)
  const userType = appContext.userType
  const restaurantId = appContext.restaurantId

  const [searchKeyword, setSearchKeyword] = useState("")
  const [editModal, setEditModal] = useState(false)
  const [food, setFood] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)

  const [items, setItems] = useState<EnhancedFood[]>([])
  const filteredItems = useMemo(() => {
    if (items.length === 0) return []
    if (searchKeyword.length === 0) return items

    return items.filter(item =>
      item.title.includes(searchKeyword) ||
      item.variations.some(v => v.title.includes(searchKeyword) || v.janCode.includes(searchKeyword))
    )
  }, [items, searchKeyword])

  const [updateCategoryOrders] = useMutation(UPDATE_FOOD_ORDERS, {
    refetchQueries: [
      {
        query: getRestaurantQuery,
        variables: { id: restaurantId }
      }
    ],
    onError: e => console.log
  })

  const toggleModal = food => {
    setEditModal(!editModal)
    setFood(food)
  }



  const getEnhancedFoodList = categories => {
    const list = []
    categories.forEach(category => {
      if (category.foods && category.foods.length) {
        category.foods.forEach(food => {
          list.push({
            ...food,
            category: category.title,
            categoryId: category._id,
            _id: food._id,
            title: food.title,
            isAvailable: food.isAvailable,
          })
        })
      }
    })
    return list
  }

  const { loading, error, data } = useRestaurantQuery(restaurantId)
  useEffect(() => {
    if (data && data.restaurant && data.restaurant.categories) {
      setItems(getEnhancedFoodList(data.restaurant.categories))
    }
  }, [data])

  if (error) {
    let message = ""
    if (error.message.indexOf("Forbidden") !== -1) message = t("Don'tHavePermission")
    else message = `${t("Error")}! ${error.message}`

    return (
      <div >
        <Header />
        <p style={{ marginTop: 30, textAlign: "center" }}>{message}</p>
      </div>
    )
  }


  function onSortEnd({ oldIndex, newIndex }) {
    if (getIsDisabled(userType)) return
    if (searchKeyword) {
      alert("フィルタリング中の並べ替えはできません")
      return
    }
    const newItems = arrayMove(items, oldIndex, newIndex)
    const newItemIds = newItems.map(item => item._id)
    setItems(newItems)
    updateCategoryOrders({
      variables: {
        restaurantId,
        foodIds: newItemIds
      }
    })
  }

  const columns = window.screen.width > PHONE_BOUNDARY
    ? [
      {
        id: "title",
        name: t('Title'),
        cell: row => <p className="mb-0 text-sm" >{row.title}</p>,
        minWidth: 200,
        maxWidth: 300,
      },
      {
        id: "description",
        name: t('Description'),
        cell: row => <p className="mb-0 text-xs" >{row.description}</p>,
        minWidth: 200,
        maxWidth: 240,
      },
      {
        id: "category",
        name: t('Category'),
        cell: row => <p className="mb-0 text-sm" >{row.category}</p>,
        maxWidth: 150,
      },
      {
        id: "janCode",
        name: t('JanCode'),
        cell: row => <p className="mb-0 text-sm" style={{ whiteSpace: "pre-wrap" }} >{row.variations.map(v => v.janCode).join("\n")}</p>,
        maxWidth: 200,
      },
      {
        id: "image",
        name: t('Image'),
        cell: row => (
          <>
            {!!row.image && (
              <img className="img-responsive" src={row.image} alt="img menu" />
            )}
            <p className="mb-0 text-sm" >{!row.image && 'No Image'}</p>
          </>
        ),
        maxWidth: 200,
      },
      {
        id: "isAvailable",
        name: t('isAvailable'),
        cell: row => <AvailableStatus row={row} />,
        maxWidth: 100,
      },
      {
        id: "action",
        name: t('Action'),
        cell: row => <ActionButtons row={row} />,
        maxWidth: 100,
      }
    ]
    : [
      {
        id: "title",
        name: t('Title'),
        cell: row => <p className="mb-0 text-sm" >{row.title}</p>,
        minWidth: 150,
        maxWidth: 300,
      },
      {
        id: "janCode",
        name: t('JanCode'),
        cell: row => <p className="mb-0 text-sm" style={{ whiteSpace: "pre-wrap" }} >{row.variations.map(v => v.janCode).join("\n")}</p>,
        maxWidth: 200,
      },
      {
        id: "image",
        name: t('Image'),
        cell: row => (
          <>
            {!!row.image && (
              <img className="img-responsive" src={row.image} alt="img menu" />
            )}
            <p className="mb-0 text-sm" >{!row.image && 'No Image'}</p>
          </>
        ),
        maxWidth: 200,
      },
      {
        id: "isAvailable",
        name: t('isAvailable'),
        cell: row => <AvailableStatus row={row} />,
        maxWidth: 100,
      },
      {
        id: "action",
        name: t('Action'),
        cell: row => <ActionButtons row={row} />,
        maxWidth: 100,
      }
    ]

  const AvailableStatus = ({ row }) => {
    const [on, setOn] = useState(true)
    useEffect(() => {
      setOn(row.isAvailable)
    }, [row])

    const [toggleFood] = useMutation(TOGGLE_FOOD, {
      refetchQueries: [
        {
          query: getRestaurantQuery,
          variables: { id: restaurantId }
        }
      ],
      onError: e => console.log
    })
    return (
      <>
        {row.isAvailable}
        <label className="custom-toggle" style={{ marginTop: 15, }}>
          <input
            checked={on}
            type="checkbox"
            onChange={event => {
              setOn(event.target.checked)
              toggleFood({
                variables: {
                  foodId: row._id,
                  restaurantId: restaurantId,
                  categoryId: row.categoryId,
                }
              })
            }}
          />
          <span className="custom-toggle-slider rounded-circle" />
        </label>
      </>
    )
  }

  const ActionButtons = ({ row }) => {
    const [deleteFood, { loading: deleteLoading }] = useMutation(DELETE_FOOD, {
      refetchQueries: [
        {
          query: getRestaurantQuery,
          variables: { id: restaurantId },
        },
      ],
      onError: e => console.log
    })
    if (deleteLoading) {
      return (
        <Loader
          type="ThreeDots"
          color="#BB2124"
          height={20}
          width={40}
          visible={deleteLoading}
        />
      )
    }
    return (
      <>
        <Badge
          style={styles.badgeStyle}
          href="#pablo"
          onClick={e => {
            e.preventDefault()
            toggleModal(row)
          }}
          color="primary">
          {t('Edit')}
        </Badge>
        {" "}
        <Badge
          style={styles.badgeStyle}
          href="#pablo"
          color="danger"
          onClick={e => {
            e.preventDefault()
            if (getIsDisabled(userType)) return
            if (window.confirm('削除してもよろしいですか？')) {
              deleteFood({
                variables: {
                  id: row._id,
                  restaurant: restaurantId,
                  categoryId: row.categoryId
                }
              })
            }
          }}>
          {t('Delete')}
        </Badge>
      </>
    )
  }

  return (
    <>
      <Header />
      {/* Page content */}
      <Container className="mt--7" fluid>
        <FoodModal
          restaurant={data && data.restaurant ? data.restaurant : null}
          modalLoading={modalLoading}
          setModalLoading={setModalLoading}
        />

        <SearchBar
          title={""}
          onSearchKeywordChange={e => setSearchKeyword(e.target.value.trim())}
          searchKeyword={searchKeyword}
          placeholder={t("FoodSearchKeywordPlaceholder")}
        />

        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              {loading
                ? <CustomLoader />
                : <SortableTable
                  items={filteredItems}
                  onSortEnd={onSortEnd}
                  columns={columns}
                />
              }
            </Card>
          </div>
        </Row>
        <Modal
          className="modal-dialog-centered"
          size="lg"
          isOpen={editModal}
          toggle={() => { if (!modalLoading) toggleModal(null) }}
        >
          <FoodModal
            food={food}
            restaurant={data && data.restaurant ? data.restaurant : null}
            modalLoading={modalLoading}
            setModalLoading={setModalLoading}
          />
        </Modal>
      </Container>
    </>
  )
}

export default Food
